import { NUTRITION_AREA_UNIT_HA } from 'constants/uiConstants';
import type {
  NutritionPlanAudit,
  NutritionSeason,
} from './computeNutritionAudit';

/** kg/ha shown to one decimal place — the precision the balance status is judged at. */
const kgPerHaFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const formatKgPerHa = (value: number): string =>
  kgPerHaFormat.format(Math.round(value * 10) / 10 || 0);

/** Balance with an explicit sign, so building and declining read apart without colour. */
export const formatSignedKgPerHa = (value: number): string => {
  const rounded = Math.round(value * 10) / 10 || 0;
  const formatted = kgPerHaFormat.format(Math.abs(rounded));
  if (rounded > 0) return `+${formatted}`;
  if (rounded < 0) return `−${formatted}`;
  return formatted;
};

/** Rate as entered, e.g. "200 kg/ha". */
export const formatRate = (rate: number, unit: string | null): string => {
  const value = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(rate);
  return unit ? `${value} ${unit}/ha` : value;
};

/** Area in ha, whole hectares above 10, otherwise one decimal place. */
export const formatAreaHa = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value >= 10 ? 0 : 1,
  }).format(value);

/** A harvest yield as entered, per ha, e.g. "3.5 t/ha". */
export const formatYield = (value: number, unit: string | null): string => {
  const formatted = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value);
  if (!unit) return formatted;
  return unit.includes('/') ? `${formatted} ${unit}` : `${formatted} ${unit}/ha`;
};

/**
 * Crops grown in a year with the area and, when recorded, the yields, e.g.
 * "Barley · 68 ha · Expected yield 3.5 t/ha". yieldLabel names which yield
 * it is. Without details it's the crop alone.
 */
export const formatSeasonCrops = (
  season: NutritionSeason,
  yieldLabel: string,
  includeDetails = true
): string => {
  const parts = [season.cropNames.join(' + ')];
  if (!includeDetails) return parts[0];
  if (season.areaHa > 0) {
    parts.push(`${formatAreaHa(season.areaHa)} ${NUTRITION_AREA_UNIT_HA}`);
  }
  if (season.yields.length > 0) {
    const yields = season.yields
      .map((y) => formatYield(y.value, y.unit))
      .join(', ');
    parts.push(`${yieldLabel} ${yields}`);
  }
  return parts.join(' · ');
};

/** One year's crops for a rotation line — two plans in a year read "Wheat + Sorghum". */
export const formatYearCrops = (season: NutritionSeason): string =>
  season.cropNames.join(' + ');

/** A nutrient analysis as recorded, e.g. "N 46% · S 0.9%". */
export const formatAnalysis = (
  analysis: Partial<Record<string, number>>,
  labels: Record<string, string>
): string =>
  Object.entries(analysis)
    .map(
      ([key, pct]) =>
        `${labels[key]} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(pct ?? 0)}%`
    )
    .join(' · ');

/**
 * One plan's line: crop, its own area and, when recorded, its yield — e.g.
 * "Barley · 54 ha · Expected yield 3 t/ha". Without the crop when it's
 * already the group heading (grouped by crop).
 */
export const formatPlanSummary = (
  plan: NutritionPlanAudit,
  yieldLabel: string,
  includeCrop = true
): string => {
  const parts = [
    ...(includeCrop ? [plan.cropName] : []),
    `${formatAreaHa(plan.areaHa)} ${NUTRITION_AREA_UNIT_HA}`,
  ];
  if (plan.yields.length > 0) {
    const yields = plan.yields
      .map((y) => formatYield(y.value, y.unit))
      .join(', ');
    parts.push(`${yieldLabel} ${yields}`);
  }
  return parts.join(' · ');
};
